import axios from 'axios';
import React, { Fragment, useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FaTimesCircle, FaEye, FaSearch, FaArrowLeft, FaShoppingCart, FaTruck, FaCheckCircle } from 'react-icons/fa';
import LoadingScreen from '../../components/LoadingScreen';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3500';

function CancelledOrdersPage() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [sortOrder] = useState('newest'); // default sorting
    const [counts, setCounts] = useState({
        pendingOrdersCount: 0,
        confirmedOrdersCount: 0,
        shippedOrdersCount: 0,
        deliveredOrdersCount: 0,
        cancelledOrdersCount: 0
    });
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 30,
        hasNextPage: false,
        hasPrevPage: false
    });

    const handleApiError = (error) => {
        console.error('API Error details:', error);
        alert(error.response?.data?.message || 'Something went wrong. Please try again.');
    };

    const fetchCancelledOrders = async (page = 1, search = '', sort = 'newest', append = false, start = startDate, end = endDate) => {
        if (append) {
            setLoadingMore(true);
        } else {
            setLoading(true);
        }
        try {
            const token = localStorage.getItem('authToken');
            const params = new URLSearchParams({
                page: page.toString(),
                limit: '30',
                search: search,
                sort: sort,
                startDate: start,
                endDate: end
            });

            const [ordersRes, countsRes] = await Promise.all([
                axios.get(`${API_BASE_URL}/cancelled-orders?${params}`, {
                    headers: { Authorization: `Bearer ${token}` }
                }),
                axios.get(`${API_BASE_URL}/counts`, {
                    headers: { Authorization: `Bearer ${token}` }
                })
            ]);

            const newOrders = ordersRes.data.orders || [];
            if (append) {
                setOrders(prev => [...prev, ...newOrders]);
            } else {
                setOrders(newOrders);
            }
            setPagination(ordersRes.data.pagination);
            setCounts(countsRes.data);
        } catch (error) {
            handleApiError(error);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    // Debounced search function
    const debouncedSearch = useCallback(
        (() => {
            let timeoutId;
            return (value, start, end) => {
                clearTimeout(timeoutId);
                timeoutId = setTimeout(() => {
                    setCurrentPage(1);
                    fetchCancelledOrders(1, value, sortOrder, false, start, end);
                }, 500);
            };
        })(),
        [sortOrder]
    );

    const handleSearch = (value) => {
        setSearchTerm(value);
        debouncedSearch(value, startDate, endDate);
    };

    const handleDateChange = (type, value) => {
        let newStart = startDate;
        let newEnd = endDate;
        if (type === 'start') {
            newStart = value;
            setStartDate(value);
        } else {
            newEnd = value;
            setEndDate(value);
        }
        setCurrentPage(1);
        fetchCancelledOrders(1, searchTerm, sortOrder, false, newStart, newEnd);
    };

    const handleClearDates = () => {
        setStartDate('');
        setEndDate('');
        setCurrentPage(1);
        fetchCancelledOrders(1, searchTerm, sortOrder, false, '', '');
    };

    const handleBackButtonClick = () => {
        setSearchTerm('');
        setStartDate('');
        setEndDate('');
        setCurrentPage(1);
        fetchCancelledOrders(1, '', 'newest', false, '', '');
    };

    useEffect(() => {
        fetchCancelledOrders(1, '', 'newest', false, '', '');
    }, []);

    // Scroll listener for Infinite Scroll
    useEffect(() => {
        const handleScroll = () => {
            const threshold = 150;
            const isNearBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - threshold;
            
            if (isNearBottom && !loading && !loadingMore && pagination.hasNextPage) {
                const nextPage = currentPage + 1;
                setCurrentPage(nextPage);
                fetchCancelledOrders(nextPage, searchTerm, sortOrder, true, startDate, endDate);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [loading, loadingMore, pagination.hasNextPage, currentPage, searchTerm, sortOrder, startDate, endDate]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0
        }).format(amount);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const orderStatusTiles = [
        { status: 'pending', title: 'PENDING ORDERS', count: counts.pendingOrdersCount, link: '/admin/pending-orders', icon: FaShoppingCart },
        { status: 'confirmed', title: 'CONFIRMED ORDERS', count: counts.confirmedOrdersCount, link: '/admin/confirmed-orders', icon: FaCheckCircle },
        { status: 'shipped', title: 'SHIPPED ORDERS', count: counts.shippedOrdersCount, link: '/admin/shipped-orders', icon: FaTruck },
        { status: 'delivered', title: 'DELIVERED ORDERS', count: counts.deliveredOrdersCount, link: '/admin/delivered-orders', icon: FaCheckCircle },
        { status: 'cancelled', title: 'CANCELLED ORDERS', count: counts.cancelledOrdersCount, link: '/admin/cancelled-orders', icon: FaTimesCircle }
    ];

    const totalOrders = orderStatusTiles.reduce((s, t) => s + (t.count || 0), 0);

    return (
        <Fragment>
            <div className="container-fluid p-3 pt-2 g-0 adma">
                <div className="row mb-3">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center">
                            <div className="section-title">
                                <h4 className="mt-1">CANCELLED ORDERS</h4>
                                <p className="text-muted p-0 m-0">View cancelled orders</p>
                            </div>
                            <Link 
                                to="/admin/orders" 
                                className="back-btn-pill"
                                title="Back to Orders"
                                onClick={handleBackButtonClick}
                            >
                                <FaArrowLeft />
                                Back to Orders
                            </Link>
                        </div>
                    </div>
                </div>

                <div className="row g-0 order-tiles-row mb-2">
                    {orderStatusTiles.map((item) => {
                        const Icon = item.icon;
                        const pct = totalOrders ? ((item.count / totalOrders) * 100).toFixed(1) : 0;
                        const isActive = item.status === 'cancelled';
                        return (
                            <div key={item.status} className="col-12 col-sm-6 col-md-4 col-xl">
                                <Link to={item.link} className={`order-tile-card order-tile-${item.status} ${isActive ? 'active' : ''} text-decoration-none`}>
                                    <div className="order-tile-top d-flex justify-content-between align-items-start mb-2">
                                        <div className="order-tile-icon-wrapper"><Icon className="order-tile-icon" /></div>
                                        <span className="order-tile-count">{item.count}</span>
                                    </div>
                                    <h6 className="order-tile-title mb-1">{item.title}</h6>
                                    <div className="order-tile-progress mt-2" aria-label="Status share" aria-valuenow={pct} aria-valuemin="0" aria-valuemax="100">
                                        <div className="order-tile-progress-bar" style={{ width: pct + '%' }}></div>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center mt-3 small">
                                        <span className="text-muted">{pct}%</span>
                                        <span className="order-tile-link-label">VIEW</span>
                                    </div>
                                </Link>
                            </div>
                        );
                    })}
                </div>

                {(totalOrders > 0 || searchTerm || startDate || endDate) && (
                    <div className="pending-control-panel" style={{ position: 'relative', zIndex: 1 }}>
                        <div className="search-control-wrapper flex-grow-1" style={{ maxWidth: '400px' }}>
                            <input
                                type="text"
                                className="form-control search-input-custom w-100"
                                placeholder="Search by name, mobile, address..."
                                value={searchTerm}
                                onChange={(e) => handleSearch(e.target.value)}
                            />
                            <FaSearch className="search-icon-custom" />
                        </div>
                        
                        <div className="d-flex flex-wrap align-items-center gap-3">
                            <div className="d-flex align-items-center gap-2">
                                <span className="text-muted small fw-semibold text-nowrap m-0">Date Range:</span>
                                <input
                                    type="date"
                                    className="form-control date-input-custom"
                                    value={startDate}
                                    onChange={(e) => handleDateChange('start', e.target.value)}
                                    max={endDate || undefined}
                                />
                                <span className="text-muted small fw-semibold text-nowrap m-0">to</span>
                                <input
                                    type="date"
                                    className="form-control date-input-custom"
                                    value={endDate}
                                    onChange={(e) => handleDateChange('end', e.target.value)}
                                    min={startDate || undefined}
                                />
                                {(startDate || endDate) && (
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary rounded-pill px-3 py-1 ms-1 text-nowrap fw-semibold"
                                        style={{ fontSize: '0.78rem' }}
                                        onClick={handleClearDates}
                                    >
                                        Clear Dates
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {loading && currentPage === 1 ? (
                    <LoadingScreen message="Loading cancelled orders..." />
                ) : (
                    <div className="row g-0">
                        <div className="col-12">
                            {orders.length > 0 ? (
                                <div className="card border-0 rounded-0 shadow-none order-table-card" style={{ position: 'relative', zIndex: 2 }}>
                                    <div className="card-body p-0">
                                        <div className="table-responsive" style={{ overflow: 'visible' }}>
                                            <table className="table table-hover mb-0">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th className="border-0">Sr. No.</th>
                                                        <th className="border-0">Order Info</th>
                                                        <th className="border-0">Customer Details</th>
                                                        <th className="border-0">Items & Amount</th>
                                                        <th className="border-0">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {orders.map((item, index) => (
                                                        <tr key={item._id}>
                                                            {/* Column 0: Sr. No. */}
                                                            <td className="align-middle text-muted small fw-semibold">
                                                                {index + 1}
                                                            </td>

                                                            {/* Column 1: Order Info */}
                                                            <td className="align-middle">
                                                                <div>
                                                                    <span className="fw-bold text-dark">
                                                                        {item.token ? item.token.toUpperCase() : 'N/A'}
                                                                    </span>
                                                                    <div className="text-muted small mt-1">
                                                                        {formatDate(item.createdAt)}
                                                                    </div>
                                                                </div>
                                                            </td>

                                                            {/* Column 2: Customer Details */}
                                                            <td className="align-middle">
                                                                <div>
                                                                    <strong className="text-dark">{item.address?.name || 'N/A'}</strong>
                                                                    <div className="text-muted small mt-1">
                                                                        <span className="text-primary fw-semibold">{item.mobile || 'N/A'}</span>
                                                                        {item.address && (
                                                                            <>
                                                                                <span className="mx-2 text-muted">|</span>
                                                                                <span>{item.address.house}, {item.address.area}</span>
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </td>

                                                            {/* Column 3: Items & Amount */}
                                                            <td className="align-middle">
                                                                <div>
                                                                    <strong className="text-success fs-6">
                                                                        {formatCurrency((item.grandTotal !== undefined && item.grandTotal !== null) ? item.grandTotal : (item.totalAmount || 0))}
                                                                    </strong>
                                                                    <div className="mt-1">
                                                                        <div className="item-count-hover-container">
                                                                            <span className="item-count-trigger text-muted small">
                                                                                {item.items?.length || 0} {item.items?.length === 1 ? 'item' : 'items'}
                                                                            </span>
                                                                            {item.items && item.items.length > 0 && (
                                                                                <div className="item-names-popover shadow-sm" style={{ zIndex: 9999 }}>
                                                                                    <div className="popover-header fw-bold border-bottom pb-1 mb-2 text-dark" style={{ fontSize: '0.78rem' }}>ORDER ITEMS</div>
                                                                                    <div className="popover-items-list scrollbar-custom" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                                                                                        {item.items.map((prod, pIdx) => (
                                                                                            <div key={pIdx} className="popover-item d-flex justify-content-between gap-3 text-secondary py-1" style={{ fontSize: '0.75rem', borderBottom: pIdx < item.items.length - 1 ? '1px dashed #f1f5f9' : 'none' }}>
                                                                                                <span className="text-start">{prod.name}</span>
                                                                                                <span className="text-end fw-bold text-nowrap">x {prod.quantity}</span>
                                                                                            </div>
                                                                                        ))}
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="align-middle">
                                                                <div className="order-actions-wrapper">
                                                                    <Link 
                                                                        to={`/admin/orders-details/${item._id}`} 
                                                                        className="order-action-btn order-action-btn-view"
                                                                        title="View Details"
                                                                    >
                                                                        <FaEye />
                                                                    </Link>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>

                                        {loadingMore && (
                                            <div className="text-center py-4 border-top">
                                                <div className="spinner-border spinner-border-sm text-danger" role="status">
                                                    <span className="visually-hidden">Loading more...</span>
                                                </div>
                                                <span className="ms-2 text-muted small fw-semibold">Loading more orders...</span>
                                            </div>
                                        )}

                                        {!pagination.hasNextPage && orders.length > 0 && (
                                            <div className="text-center py-3 border-top bg-light text-muted small fw-semibold">
                                                All cancelled orders loaded
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="order-empty-state">
                                    <div className="order-empty-state-icon-wrapper">
                                        <FaTimesCircle className="order-empty-state-icon" />
                                    </div>
                                    <h5>No Cancelled Orders</h5>
                                    <p>Great! No orders have been cancelled by customers or administrators.</p>
                                    <Link 
                                        to="/admin/orders"
                                        className="order-empty-state-btn"
                                    >
                                        Go to Order Management
                                    </Link>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            <style>{`
                .pending-control-panel {
                    position: relative;
                    z-index: 1;
                }
                .order-table-card {
                    position: relative;
                    z-index: 2; /* Bring table container above control panel */
                }
                .item-count-hover-container {
                    position: relative;
                    display: inline-block;
                }
                .item-count-hover-container:hover {
                    z-index: 9999; /* Bring hovered cell stack to front */
                }
                .item-count-trigger {
                    cursor: pointer;
                    border-bottom: 1px dashed #cbd5e1;
                    padding-bottom: 1px;
                    transition: color 0.15s ease;
                }
                .item-count-trigger:hover {
                    color: #ed3237 !important;
                }
                .item-names-popover {
                    display: none;
                    position: absolute;
                    bottom: calc(100% + 8px); /* Position upside */
                    left: 50%;
                    transform: translateX(-50%);
                    background: #ffffff;
                    border: 1px solid #cbd5e1;
                    padding: 10px 14px;
                    z-index: 9999; /* Raise popover above all headers and controls */
                    width: max-content;
                    min-width: 200px;
                    max-width: 280px;
                    text-align: left;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
                }
                /* Downward pointing arrow */
                .item-names-popover::after {
                    content: "";
                    position: absolute;
                    top: 100%;
                    left: 50%;
                    transform: translateX(-50%);
                    border-width: 6px;
                    border-style: solid;
                    border-color: #ffffff transparent transparent transparent;
                }
                .item-names-popover::before {
                    content: "";
                    position: absolute;
                    top: 100%;
                    left: 50%;
                    transform: translateX(-50%);
                    border-width: 7px;
                    border-style: solid;
                    border-color: #cbd5e1 transparent transparent transparent;
                    z-index: -1;
                }
                .item-count-hover-container:hover .item-names-popover {
                    display: block;
                }
            `}</style>
        </Fragment>
    );
}

export default CancelledOrdersPage;
